import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, X, Loader2, Sparkles } from 'lucide-react';
import { uploadImage } from './api';

/**
 * Uploader for a single image URL field. Accepts a value (URL) and calls
 * onChange with the new absolute URL when the upload succeeds. Also allows
 * the user to type/paste a URL manually.
 *
 * When onGenerate is provided (Green Room editor), an extra "Generate with AI"
 * button appears. onGenerate is invoked with no arguments and should return
 * a Promise<{url: string}>.
 */
export default function ImageUpload({ value, onChange, label = 'Image', hint, onGenerate }) {
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadImage(file);
      onChange(url);
      toast.success('Image uploaded.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleGenerate = async () => {
    if (!onGenerate) return;
    setGenerating(true);
    try {
      const { url } = await onGenerate();
      if (url) {
        onChange(url);
        toast.success('AI cover generated.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed. Try refining the title.');
    } finally {
      setGenerating(false);
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const resolvedPreview = value
    ? (value.startsWith('http') ? value : `${backendUrl}${value}`)
    : '';

  return (
    <div>
      <label className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{label}</label>
      <div className="mt-2 flex flex-wrap items-start gap-4">
        {resolvedPreview ? (
          <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-[#faf8f4] border border-black/10 shrink-0">
            <img src={resolvedPreview} alt="preview" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute top-1 right-1 bg-black/70 hover:bg-black text-white rounded-full p-1 transition-colors"
              aria-label="Clear image"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-lg border-2 border-dashed border-black/15 bg-[#faf8f4] flex items-center justify-center text-[#6b6b6b] shrink-0">
            <Upload size={20} />
          </div>
        )}

        <div className="flex-1 min-w-[240px]">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="image-upload-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || generating}
              className="inline-flex items-center gap-2 rounded-full bg-[#002b5c] text-white px-4 py-2 text-[11px] tracking-[0.24em] uppercase hover:bg-[#001a38] disabled:opacity-60 transition-colors"
            >
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            {onGenerate && (
              <button
                type="button"
                data-testid="image-generate-btn"
                onClick={handleGenerate}
                disabled={generating || uploading}
                title="Generate an editorial cover image using AI"
                className="inline-flex items-center gap-2 rounded-full border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#001a38] px-4 py-2 text-[11px] tracking-[0.24em] uppercase disabled:opacity-60 transition-colors"
              >
                {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                {generating ? 'Generating…' : 'Generate with AI'}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
              onChange={handleFile}
              className="hidden"
            />
          </div>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an https:// URL"
            className="mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-2 px-3 outline-none text-xs font-mono"
          />
          {hint && <p className="mt-1 text-[11px] text-[#6b6b6b]">{hint}</p>}
        </div>
      </div>
    </div>
  );
}
